import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  SButton,
  SCard,
  SIcon,
  SInput,
  SNotification,
} from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { CBRetentionLogo } from '@bbkAdminComponents/cbretention-logo';
import { useInfoModal } from '@bbkAdminComponents/info-dialog';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import type * as LogInTypes from '@bbkAdminComponents/log-in-flow/log-in-types';
import { LogInTrouble } from '@bbkAdminComponents/log-in-flow/trouble';
import {
  preauthCompanySlice,
  type CheckUsernameResponse,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { Icons } from '@bbkAdminUtils/icons';

type SignInTypes = {
  error?: LogInTypes.Error;
  success?: LogInTypes.Success;
  formValues: LogInTypes.FormValues;
  googleSignIn: LogInTypes.GoogleSignIn;
  chargebeeSignIn: LogInTypes.ChargebeeSignIn;
  signIn: LogInTypes.SignIn;
  sending: LogInTypes.Sending;
  isCBPath?: boolean;
};
export const SignIn: React.FC<SignInTypes> = ({
  formValues,
  googleSignIn,
  signIn,
  chargebeeSignIn,
  error,
  sending,
  success,
  isCBPath,
}) => {
  const { email, password, setEmail, setPassword } = formValues;

  const pwdRef = useRef<HTMLInputElement>(null);
  const { triggerModal } = useInfoModal();

  const isPublicUsernamePasswordDisabled = useFeatureFlags(
    FeatureFlagsE.DISABLE_PUBLIC_USERNAME_PASSWORD_AUTH
  );
  const isDevLoginAuthEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_DEV_LOGIN_AUTH
  );
  const isCbIdpPhase4Enabled = useFeatureFlags(
    FeatureFlagsE.CHARGEBEE_IDP_PHASE_4
  );
  const isPasswordAuthAvailable =
    isDevLoginAuthEnabled || !isPublicUsernamePasswordDisabled;
  const { data: clientConfig } =
    preauthCompanySlice.endpoints.getClientConfig.useQuery();
  const isDevIdentityLogin =
    isDevLoginAuthEnabled && Boolean(clientConfig?.dev_identity_provider);
  const showGoogleSignIn = !isCbIdpPhase4Enabled && !isDevIdentityLogin;
  const [showPasswordField, setShowPasswordField] = useState<boolean>(
    isDevIdentityLogin || (isPasswordAuthAvailable && !!error)
  );
  const shouldShowPasswordAuth =
    isPasswordAuthAvailable && (isDevIdentityLogin || showPasswordField);
  const [showPassword, setShowPassword] = useState(false);

  const [cbidpCheckUsername$] =
    preauthCompanySlice.endpoints.cbidpCheckUsername.useLazyQuery();
  const [cbidpInitiateMigration$] =
    preauthCompanySlice.endpoints.cbidpInitiateMigration.useLazyQuery();

  useEffect(() => {
    if (isDevIdentityLogin) {
      setShowPasswordField(true);
    }
  }, [isDevIdentityLogin]);

  useEffect(() => {
    if (shouldShowPasswordAuth) {
      pwdRef.current?.focus();
    }
  }, [shouldShowPasswordAuth]);

  const checkUsername = useCallback(
    async (em: string) => {
      if (!em?.trim()) return;

      let isEmailAnCBIDPUser = false;
      let cbLoginBaseUrl;
      let showMigration = true;
      try {
        const response: CheckUsernameResponse = await cbidpCheckUsername$(
          { email: em },
          true
        ).unwrap();
        isEmailAnCBIDPUser = response.valid;
        cbLoginBaseUrl = response.url;
        showMigration = response?.code !== 'not_found';
      } catch (e) {
        BlackToastTrigger({
          content: 'Could not check email. Please try again',
        });
        GlobalLogger.error(e);
      } finally {
        setShowPasswordField(isPasswordAuthAvailable && !isEmailAnCBIDPUser);
        if (!isEmailAnCBIDPUser) {
          if (!isPasswordAuthAvailable && showGoogleSignIn) {
            BlackToastTrigger({
              content: 'Continue with Google or Chargebee to sign in.',
            });
          } else if (isCbIdpPhase4Enabled && showMigration) {
            setShowPasswordField(!isCbIdpPhase4Enabled);
            cbidpInitiateMigration$({ email: em })
              .unwrap()
              .then(() => {
                triggerModal({
                  title: <>Login system update</>,
                  description: (
                    <>
                      We are updating our login system to make it easier for our
                      customers to access all Chargebee products. Please check
                      your email for instructions to set up your credentials.
                    </>
                  ),
                });
              })
              .catch((e: RTKAPIErrorResponse) => {
                const err = parseRTKAPIError(e);
                BlackToastTrigger({
                  content:
                    'Failed to Initiate Account Migration. Please check your email.',
                });
                GlobalLogger.error(
                  `cbidpInitiateMigration error: ${err?.message}`
                );
              });
          }
        } else {
          chargebeeSignIn(em, cbLoginBaseUrl);
        }
      }
    },
    [
      cbidpCheckUsername$,
      chargebeeSignIn,
      cbidpInitiateMigration$,
      isCbIdpPhase4Enabled,
      isPasswordAuthAvailable,
      showGoogleSignIn,
      triggerModal,
    ]
  );

  return (
    <div
      className="sign-in-page tw-flex tw-min-h-screen tw-w-full tw-flex-col tw-items-center tw-justify-start tw-px-4 tw-py-8"
      data-testid="sign-in-page"
    >
      <div className="sign-in-card-shell tw-w-full tw-max-w-[420px]">
        <SCard
          padding="large"
          rounded
          className="!tw-border !tw-border-neutral-200"
        >
          <div className="tw-flex tw-flex-col tw-gap-5">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center">
              <CBRetentionLogo className="tw-h-10" />
              <h1 className="tw-text-heading-md tw-font-semibold tw-text-neutral-900">
                Sign-in to continue
              </h1>
            </div>

            {success && (
              <SNotification
                variant="success"
                hasBorder
                title={success.title}
                description={success.message}
              />
            )}
            {error && (
              <SNotification
                variant="danger"
                hasBorder
                title={error.title}
                description={error.message}
              />
            )}

            <form
              onSubmit={
                shouldShowPasswordAuth ? signIn : (e) => e.preventDefault()
              }
              className="tw-flex tw-flex-col tw-gap-4"
              data-testid="sign-in-form"
            >
              <SInput
                label="Email"
                type="email"
                name="email"
                autoComplete="on"
                placeholder="Enter your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => {
                  setShowPassword(false);
                  if (!isDevIdentityLogin) {
                    setShowPasswordField(false);
                  }
                }}
                disabled={sending || isCBPath}
              />

              {shouldShowPasswordAuth && (
                <SInput
                  ref={pwdRef}
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="on"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={sending}
                  append={
                    <button
                      type="button"
                      className="tw-flex tw-items-center tw-justify-center tw-text-neutral-500 hover:tw-text-neutral-700 focus:tw-text-neutral-900 focus:tw-outline-none"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? 'Hide' : 'Show'}
                      disabled={sending}
                    >
                      <SIcon
                        name={showPassword ? 'eye-off' : 'eye'}
                        size={18}
                      />
                    </button>
                  }
                />
              )}

              {shouldShowPasswordAuth ? (
                <SButton
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  className="submitButtonFooter"
                  disabled={sending}
                  loading={sending}
                  icon={<SIcon name="arrow-right" size={16} />}
                  iconPosition="right"
                >
                  Sign in
                </SButton>
              ) : (
                <SButton
                  type="button"
                  variant="primary"
                  size="large"
                  fullWidth
                  className="submitButtonFooter"
                  disabled={sending}
                  icon={<SIcon name="arrow-right" size={16} />}
                  iconPosition="right"
                  onClick={() => {
                    void checkUsername(email);
                  }}
                >
                  Continue
                </SButton>
              )}

              {showGoogleSignIn && (
                <>
                  <div className="tw-flex tw-items-center tw-gap-3 tw-py-1">
                    <div className="tw-h-px tw-flex-1 tw-bg-neutral-200" />
                    <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.08em] tw-text-neutral-500">
                      or
                    </span>
                    <div className="tw-h-px tw-flex-1 tw-bg-neutral-200" />
                  </div>
                  <SButton
                    type="button"
                    variant="neutral-outline"
                    size="large"
                    fullWidth
                    disabled={sending}
                    onClick={googleSignIn}
                    icon={
                      <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center">
                        {Icons.google}
                      </span>
                    }
                  >
                    Sign in with Google
                  </SButton>
                </>
              )}
            </form>
          </div>
        </SCard>
      </div>

      {!isPublicUsernamePasswordDisabled && !isDevLoginAuthEnabled && (
        <div className="tw-mt-6">
          <LogInTrouble
            link={{ path: LogInPaths.forgotPassword, copy: 'Forgot password?' }}
          />
        </div>
      )}
    </div>
  );
};
